import { AxiosInstance } from 'axios';
import baseAxios from '@/helpers/forms/axios';
import { userInterface } from '../users';

export type PrinterRawType = 'epl' | 'zpl' | 'none';

export interface printerInfoInterface {
    identifier: string
    brand?: string
    widthCm: string
    heightCm: string
    density: string
    rawType: PrinterRawType
}

export interface stationInterface {
    id: number
    userUuid: string | null
    user?: userInterface
    name: string
    hostAddress: string
    scanner?: string
    shippingLabelPrinter?: string
    shippingLabelPrinterInfo?: printerInfoInterface
    barcodeLabelPrinter?: string
    barcodeLabelPrinterInfo?: printerInfoInterface
    paperPrinter?: string
    scale?: string
    scannerConfiguration?: object
    shippingLabelPrinterConfiguration?: {
        density?: 80 | 120
        orientation?: 'auto' | 'landscape' | 'portrait'
        rawType?: PrinterRawType
    }
    barcodeLabelPrinterConfiguration?: {
        size?: string
        flip?: boolean
        density?: 80 | 120
        orientation?: 'auto' | 'landscape' | 'portrait'
        useDivider?: boolean
        rawType?: PrinterRawType
    }
    paperPrinterConfiguration?: object
    scaleConfiguration?: object
}

export interface StationPayload {
    userId: number | null
    name: string
    hostAddress: string
    scanner: string
    shippingLabelPrinter: string
    shippingLabelPrinterConfiguration: {
        size: string
        flip: boolean
        orientation: string
        density: number
        rawType: PrinterRawType
    }
    barcodeLabelPrinter: string
    barcodeLabelPrinterConfiguration: {
        size: string
        flip: boolean
        orientation: string
        density: number
        useDivider: boolean
        rawType: PrinterRawType
    }
    paperPrinter: string
    scale: string
}

// The backend replaces a device's configuration blob wholesale instead of merging
// it, so every key must be present on every save or the omitted ones are wiped.
const stationRequestPayload = (payload: StationPayload): StationPayload => ({
    userId: payload.userId,
    name: payload.name,
    hostAddress: payload.hostAddress,
    scanner: payload.scanner,
    shippingLabelPrinter: payload.shippingLabelPrinter,
    shippingLabelPrinterConfiguration: {
        size: payload.shippingLabelPrinterConfiguration.size,
        flip: payload.shippingLabelPrinterConfiguration.flip,
        orientation: payload.shippingLabelPrinterConfiguration.orientation,
        density: payload.shippingLabelPrinterConfiguration.density,
        rawType: payload.shippingLabelPrinterConfiguration.rawType,
    },
    barcodeLabelPrinter: payload.barcodeLabelPrinter,
    barcodeLabelPrinterConfiguration: {
        size: payload.barcodeLabelPrinterConfiguration.size,
        flip: payload.barcodeLabelPrinterConfiguration.flip,
        orientation: payload.barcodeLabelPrinterConfiguration.orientation,
        density: payload.barcodeLabelPrinterConfiguration.density,
        useDivider: payload.barcodeLabelPrinterConfiguration.useDivider,
        rawType: payload.barcodeLabelPrinterConfiguration.rawType,
    },
    paperPrinter: payload.paperPrinter,
    scale: payload.scale,
});

export default (axios: AxiosInstance = baseAxios): { fetchStation: (stationId: number) => Promise<stationInterface>; fetchStations: () => Promise<stationInterface[]>; fetchCurrentStation: () => Promise<stationInterface>; createStation: (payload: StationPayload) => Promise<void>; updateStation: (stationId: number, payload: StationPayload) => Promise<void>; deleteStation: (stationId: number) => Promise<void>; } => ({
    fetchStation: async (stationId: number): Promise<stationInterface> => {
        const response = await axios.get<{ station: stationInterface }>(`/api/staff/stations/${stationId}`);

        return response.data.station;
    },

    fetchStations: async (): Promise<stationInterface[]> => {
        const response = await axios.get<{ stations: stationInterface[] }>('/api/staff/stations');

        return response.data.stations;
    },

    fetchCurrentStation: async (): Promise<stationInterface> => {
        const response = await axios.get<{ data: stationInterface }>('/api/staff/stations/current');

        return response.data.data;
    },

    createStation: async (payload: StationPayload): Promise<void> => {
        await axios.post('/api/staff/stations', stationRequestPayload(payload));
    },

    updateStation: async (stationId: number, payload: StationPayload): Promise<void> => {
        await axios.put(`/api/staff/stations/${stationId}`, stationRequestPayload(payload));
    },

    deleteStation: async (stationId: number): Promise<void> => {
        await axios.delete(`/api/staff/stations/${stationId}`);
    },
});
